// src/preview/spec.ts
// The parts of FbAdPipeline/src/make_video.py the browser draft renderer
// needs to read a spec the same way: DEFAULTS and deep_merge, the walk over
// text boxes and images, and the small layout helpers. Ported line for line;
// keep the two in step (the parity test in ring/worker/tests catches drift).
// ─────────────────────────────────────────────────────────────────

export type Json = Record<string, any>;

export const DEFAULTS: Json = {
  size: [1080, 1920],
  fps: 30,
  background: { zoom: { target: 'center', from: 1.0, to: 1.25, ease: 'linear' } },
  audio: { start: 0, fadeIn: 0.3, fadeOut: 1.0, loudnorm: false },
  style: {
    template: 'default', fontSize: 'auto', y: 50, anchor: 'middle', safeZone: true,
    color: '#ffffff', accent: '#f5c542',
    width: 100, textStyle: 'shadow', pillColor: 'rgba(0,0,0,0.6)', dim: 0.25, fade: 0.25,
  },
  endCard: { mode: 'overlay', duration: 3.0, y: 50, scale: 0.8, fit: 'contain', pad: 'blur' },
};

/** Default y for endCard.text (below an image centred at the default y=50). */
export const END_TEXT_Y = 75;
/** beats[].images[].scale: fraction of canvas width. */
export const DEFAULT_IMAGE_SCALE = 0.8;
/** background.overlays[] item defaults (applied per item; deepMerge doesn't reach into lists). */
export const DEFAULT_OVERLAY: Json = { x: 4, y: 15, anchor: 'upper-left', scale: 0.2, opacity: 1, during: 'all' };
export const KINDLE_ASPECT = 1.2;

/** 3x3 grid vocabulary for the zoom target: (x, y) anchors in {0, .5, 1}. */
export const GRID: Record<string, [number, number]> = {
  'upper-left':  [0, 0],   'upper-center':  [0.5, 0],   'upper-right':  [1, 0],
  'middle-left': [0, 0.5], 'center':        [0.5, 0.5], 'middle-right': [1, 0.5],
  'lower-left':  [0, 1],   'lower-center':  [0.5, 1],   'lower-right':  [1, 1],
};

export const FLEX: Record<string, string> = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };

/**
 * Python's round(): halves go to the even neighbour (round(22.5) == 22),
 * where Math.round goes up. Every round() in make_video's layout maths
 * must use this, or a card can come out a pixel off.
 */
export function pyRound(x: number): number {
  const r = Math.round(x);
  return Math.abs(x - Math.trunc(x)) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

function isPlainObject(v: unknown): v is Json {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function deepMerge(base: Json, over: Json): Json {
  const out: Json = structuredClone(base);
  for (const [k, v] of Object.entries(over)) {
    out[k] = isPlainObject(v) && isPlainObject(out[k]) ? deepMerge(out[k], v) : structuredClone(v);
  }
  return out;
}

/** The text boxes of a beat: its `texts` items, or else the beat itself. */
export function beatBoxes(beat: Json): Json[] {
  return 'texts' in beat ? beat.texts : [beat];
}

/** Every text box in the video as [beat index, box], in render order. */
export function textBoxes(spec: Json): [number, Json][] {
  return (spec.beats as Json[]).flatMap((b, i) => beatBoxes(b).map(box => [i, box] as [number, Json]));
}

/** Every beats[].images[] entry as [beat index, image]. */
export function beatImages(spec: Json): [number, Json][] {
  return (spec.beats as Json[]).flatMap((b, i) => (b.images ?? []).map((im: Json) => [i, im] as [number, Json]));
}

/** background.overlays[] with DEFAULT_OVERLAY filled in. */
export function backgroundOverlays(spec: Json): Json[] {
  const overlays = spec.background?.overlays;
  return Array.isArray(overlays) ? overlays.map((o: Json) => ({ ...DEFAULT_OVERLAY, ...o })) : [];
}

/** Largest w x h box an image of `scale` may fill: that fraction of the canvas width, no taller than 85% of it of the height. */
export function imageBox(w: number, h: number, scale: number): [number, number] {
  return [pyRound(w * scale), pyRound(h * scale * 0.85)];
}

/** endCard.text as a beat-like object, or null. */
export function endTextBeat(spec: Json): Json | null {
  const end = spec.endCard ?? {};
  if (!end.image || !('text' in end)) return null;
  const t = end.text;
  const beat: Json = isPlainObject(t) ? { ...t } : { text: t };
  if (!('y' in beat)) beat.y = END_TEXT_Y;
  return beat;
}

/** Index of the first occurrence of phrase in HTML text that doesn't touch a tag, or -1. */
export function findPhrase(text: string, phrase: string): number {
  const tags = [...text.matchAll(/<[^>]*>/g)].map(m => [m.index!, m.index! + m[0].length]);
  let i = text.indexOf(phrase);
  while (i >= 0) {
    if (!tags.some(([a, b]) => i < b && i + phrase.length > a)) return i;
    i = text.indexOf(phrase, i + 1);
  }
  return -1;
}
